import { z } from 'zod';
import { zId, zMoney } from './primitives.js';

/**
 * Consignment Invoice — the parent of a shipment (docs/plans/consignment-invoice-parent).
 *
 * **D97, release 1.** This document was called the *purchase invoice* until 31-Oct-2026. It is
 * the DESPATCH document, and it is not the *purchase invoice* (`payables.ts`), which is raised over
 * pieces a retailer reported sold and is **not** renamed in this release.
 *
 * One retailer and one currency per invoice (PI-1). The quantities default to
 * whatever is still unclaimed on each order line, so `lines` is only sent to
 * invoice less than that.
 */
/**
 * The states a consignment invoice can be in — **the single declaration of these three values
 * in the repository.**
 *
 * Declared here, in `packages/contracts`, and not in the drizzle schema, because this is the
 * only package the NestJS route, the React screens and the tests can all import (CLAUDE.md:
 * *a field is defined once and consumed by the route, the form and the tests*).
 * `apps/api/src/db/schema/consignment-invoice.ts` imports it for its `text('state', { enum })`,
 * and `apps/web/src/api/types.ts` re-exports the type rather than hand-writing the union — a
 * response type in that file is a CLAIM, not a check, and a hand-written copy of this union
 * would go stale the day a fourth state lands with nothing to say so.
 *
 * **SH-14's terminal state is not built (D92 open, 31-Oct-2026).** When it is decided it is
 * added HERE, and only here. That is one line, plus one i18n key at
 * `consignmentInvoice.state.<NAME>` — no screen edit, because no screen holds a label map.
 * Every `Record<ConsignmentInvoiceState, …>` over this union fails typecheck the moment a
 * member is added, which is the right behaviour: it forces a deliberate choice rather than a
 * silent default.
 *
 * Do NOT add a fourth value to anticipate D92, and do not guess its name. `CLOSED`, `COMPLETE`
 * and `SETTLED` are each already taken by a different meaning in this system.
 */
export const consignmentInvoiceStates = ['OPEN', 'SHIPMENT_CREATED', 'CANCELLED'] as const;
export type ConsignmentInvoiceState = (typeof consignmentInvoiceStates)[number];

export const zCreateConsignmentInvoice = z.object({
  orderIds: z.array(zId).min(1, 'A consignment invoice needs at least one order'),
  lines: z.array(z.object({
    orderLineId: zId,
    quantity: z.number().int().positive(),
  })).optional(),
});
export type CreateConsignmentInvoice = z.infer<typeof zCreateConsignmentInvoice>;

/** Admin chooses the invoiced quantity. 0 takes a line off; the rest stays available. */
export const zUpdateConsignmentInvoiceLines = z.object({
  lines: z.array(z.object({
    orderLineId: zId,
    quantity: z.number().int().nonnegative(),
  })).min(1, 'A consignment invoice needs at least one line'),
});
export type UpdateConsignmentInvoiceLines = z.infer<typeof zUpdateConsignmentInvoiceLines>;

export const zCancelConsignmentInvoice = z.object({
  reason: z.string().trim().min(3, 'A reason is required to cancel').max(1000),
});
export type CancelConsignmentInvoice = z.infer<typeof zCancelConsignmentInvoice>;

/**
 * Creating the shipment from an invoice. The courier name is required and the tracking
 * number optional. There is NO courier charge any more (client direction, 04-Nov-2026: Miraki
 * does not charge a consignment retailer for the courier): the field is gone from this body, so a
 * stale client that still sends one has it stripped, and the server stores 0.
 *
 * **Not renamed by D97, deliberately.** "Invoice" here is unqualified and this schema is
 * reached only from the consignment side, so renaming it would widen the diff for nothing.
 */
export const zCreateShipmentFromInvoice = z.object({
  courierName: z.string().trim().min(1, 'Courier name is required').max(120),
  trackingNumber: z.string().trim().max(120).optional(),
});
export type CreateShipmentFromInvoice = z.infer<typeof zCreateShipmentFromInvoice>;

/* ------------------------------------------------------------------------------------
 * D97 Wave 0 — the old names, kept alive for one wave only.
 *
 * Wave 1's four sessions each migrate their own call sites against a green typecheck; none
 * of them may edit this file. **Wave 3 deletes everything below this line, in the same
 * release**, and the vocabulary gate enforces it — these names are allow-listed as
 * transitional entries, and an allow-list entry that matches nothing is itself a gate
 * failure (exit 2). The scaffolding cannot be left up.
 * ----------------------------------------------------------------------------------- */




